import React, { useMemo } from 'react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Skeleton } from 'primereact/skeleton'
import { useTranslation } from 'react-i18next'
import {
  orderDetailsColumn,
  patientDemographicsColumn
} from '../constants/groupedPatientOrderColumns'
import { useSelector } from 'react-redux'

const normalizeHeader = header => {
  if (typeof header === 'string')
    return header.toLowerCase().replace(/\s+/g, ' ').trim()
  return ''
}

const normalizedTranslatedHeaders = (t, labels) =>
  labels.map(label => normalizeHeader(t(label)))

const shouldGroupPatientOrderColumns = (columns, t) => {
  const fields = columns.map(col => String(col?.field || '').toLowerCase())
  const headers = columns.map(col => normalizeHeader(col?.header))
  const orderHeaders = [
    'order no',
    'order number',
    'order type',
    ...normalizedTranslatedHeaders(t, [
      'Order No',
      'Order Number',
      'Order Type'
    ])
  ]

  return (
    fields.some(field =>
      ['order_no', 'order_number', 'order_source'].includes(field)
    ) || headers.some(header => orderHeaders.includes(header))
  )
}

const isGroupedPatientOrderColumn = (col, t) => {
  const field = String(col?.field || '').toLowerCase()
  const header = normalizeHeader(col?.header)
  const groupedHeaders = [
    'order no',
    'order number',
    'date',
    'order date',
    'order type',
    'type',
    'patient name',
    'file no',
    'fileno',
    'file number',
    'document id',
    'documentid',
    'contact',
    'contact no',
    'phone',
    'phone no',
    'age',
    'gender',
    'beneficiary type',
    'practitioner',
    'practioner',
    ...normalizedTranslatedHeaders(t, [
      'Order No',
      'Order Number',
      'Date',
      'Order Date',
      'Order Type',
      'Type',
      'Patient Name',
      'File No',
      'FileNo',
      'File Number',
      'Document ID',
      'DocumentId',
      'Contact',
      'Contact No',
      'Phone',
      'Phone No',
      'Age',
      'Gender',
      'Beneficiary Type',
      'Practitioner',
      'Practioner'
    ])
  ]

  return (
    [
      'patient_demographics',
      'order_details',
      'order_no',
      'order_number',
      'order_source',
      'patient_name',
      'file_number',
      'document_id',
      'phone',
      'contact',
      'beneficiary_type',
      'practitioner',
      'patient.name',
      'patient.file_no',
      'patient.document_id',
      'patient.phone',
      'patient.age',
      'patient.gender',
      'patient.beneficiary_type',
      'practitioner.name'
    ].includes(field) || groupedHeaders.includes(header)
  )
}

const groupPatientOrderColumns = (columns, t, settings, onPatientClick) => {
  if (settings === 'row_wise_details') return columns
  if (!shouldGroupPatientOrderColumns(columns, t)) return columns
  if (
    columns.some(col =>
      ['patient_demographics', 'order_details'].includes(col?.field)
    )
  )
    return columns

  const firstGroupedIndex = columns.findIndex(col =>
    isGroupedPatientOrderColumn(col, t)
  )
  const remainingColumns = columns.filter(
    col => !isGroupedPatientOrderColumn(col, t)
  )
  const insertIndex = firstGroupedIndex < 0 ? 0 : firstGroupedIndex
  const groupedColumns = [
    patientDemographicsColumn(t, onPatientClick),
    orderDetailsColumn(t)
  ]

  return [
    ...remainingColumns.slice(0, insertIndex),
    ...groupedColumns,
    ...remainingColumns.slice(insertIndex)
  ]
}

const CustomTable = ({
  data,
  isLoading,
  columns = [],
  tableStyle = { minWidth: '50rem' },
  paginator = false,
  emptyMessage = 'NoResultsFound.',
  rootyStyles = 'overflow-auto border border-gray-200 rounded border-b-0 custom-table',
  rowClassName = '',
  onPatientClick = null,
  onRowClick = () => {},
  rest
}) => {
  const { t, i18n } = useTranslation()
  const settings = useSelector(
    state => state?.settings?.map?.order_list_layout?.value
  )
  const visibleColumns = useMemo(
    () => groupPatientOrderColumns(columns || [], t, settings, onPatientClick),
    [columns, t, settings, onPatientClick]
  )
  return (
    <DataTable
      onRowClick={onRowClick}
      value={isLoading ? Array(7).fill({}) : Array.isArray(data) ? data : []}
      tableStyle={tableStyle}
      paginator={paginator}
      emptyMessage={t(emptyMessage)}
      rowClassName={rowClassName}
      pt={{
        root: { className: rootyStyles },
        loadingIcon: { className: 'text-primary' },
        loadingOverlay: { className: 'bg-[rgba(0,0,0,0.2)]' },
        wrapper: { className: 'scrollbar' }
      }}
      {...rest}
    >
      {visibleColumns?.map((col, index) => (
        <Column
          key={index}
          field={col?.field}
          header={col?.header}
          body={
            isLoading ? <Skeleton width='100%' height='1.5rem' /> : col?.body
          }
          style={{ ...col.style }}
          pt={{
            headerCell: {
              className: `!px-[0.35rem] !py-[0.6rem] bg-gray-300 ${
                i18n.language === 'ar' ? 'text-right' : 'text-left'
              } ${col.headerCellClass || ''}`,
              ...col.pt?.headerCell
            },
            headerTitle: {
              className: `text-14-dark !font-[600] whitespace-nowrap ${
                col.headerTitleClass || ''
              }`
            },
            bodyCell: {
              className: `!px-[0.35rem] !py-[0.6rem] text-14-gray leading-[1.25rem] border-b !border-b-gray-200 whitespace-pre-line ${
                i18n.language === 'ar' ? 'text-right' : 'text-left'
              } ${col.bodyCellClass || ''} `,
              ...col.pt?.bodyCell
            },
            ...col.pt
          }}
          {...col.extraProps}
        />
      ))}
    </DataTable>
  )
}

export default CustomTable
